import { ObjecttenancyTenancy, TypeDate } from '@/types/api'
import {
  findLatestPreviousNonVacancyTenancy,
  isBoundedHistoricalTenancy,
  isHistoricalTenancyMissingEndDate,
  isMissingTenancyDate,
  typeDateToComparableInput
} from './tenancyUtils'

export type TenancyDateConflictKey =
  | 'message.savingErrors.tenancyStartingAtAfterEndingAt'
  | 'message.savingErrors.sameDateOfTenanciesError'
  | 'message.savingErrors.sameDateTenanciesError'
  | 'message.savingErrors.tenancyEndAtCanNotBeDeleted'
  | 'message.addVacancy.startsBeforePrevious'
  | 'message.savingErrors.vacancyEndAtCanNotBeDeleted'
  | 'message.savingErrors.tenancyStartingOverLap'
  | 'message.savingErrors.reasonVacancyOnTwoVacancies'

/** Onboarding rows may carry runtime draft ids that are not in the generated API types. */
export type TenancyWithDraftId = ObjecttenancyTenancy & {
  internalId?: string | number
  isNewItem?: boolean
}

function typeDateToMs(value: TypeDate | null | undefined | number): number | null {
  const input = typeDateToComparableInput(value)
  return input ? new Date(input).getTime() : null
}

function isEndingAtSet(value: TypeDate | null | undefined | number): boolean {
  return !isMissingTenancyDate(value)
}

function isSameTypeDate(a: TypeDate | null | undefined, b: TypeDate | null | undefined): boolean {
  if (a == null || b == null) {
    return a == null && b == null
  }
  return a.day === b.day && a.month === b.month && a.year === b.year
}

function isSameTenancyRow(a: TenancyWithDraftId, b: TenancyWithDraftId): boolean {
  if (!a || !b) {
    return false
  }
  if (a.id && b.id && a.id === b.id) {
    return true
  }
  return !!(a.internalId && b.internalId && a.internalId === b.internalId)
}

function isDebitUntilAfterStart(debitUntil: TypeDate | null | undefined, start: TypeDate | null | undefined): boolean {
  const debitUntilMs = typeDateToMs(debitUntil)
  const startMs = typeDateToMs(start)
  return debitUntilMs != null && startMs != null && debitUntilMs > startMs
}

/**
 * Match the existing overlap() mixin logic used by CreateObjectsTab.
 * Ranges are sorted by start date ascending; overlap is reported when any
 * previous range touches the current range.
 */
function hasOverlap(ranges: { start: TypeDate | null | undefined; end: TypeDate | null | undefined }[]): boolean {
  const sorted = [...ranges].sort((a, b) => {
    const aMs = typeDateToMs(a.start) ?? 0
    const bMs = typeDateToMs(b.start) ?? 0
    return aMs - bMs
  })
  for (let i = 1; i < sorted.length; i++) {
    const previous = sorted[i - 1]
    const current = sorted[i]
    const previousStartMs = typeDateToMs(previous.start)
    const previousEndMs = typeDateToMs(previous.end)
    const currentStartMs = typeDateToMs(current.start)
    const currentEndMs = typeDateToMs(current.end)
    if (previousStartMs == null || currentStartMs == null) {
      continue
    }
    let overlap = false
    if (previousEndMs == null && currentEndMs == null) {
      overlap = false
    } else if (currentEndMs == null) {
      overlap = previousEndMs != null && previousEndMs >= currentStartMs
    } else if (previousEndMs == null) {
      overlap = previousStartMs >= currentEndMs
    } else {
      overlap = previousStartMs <= currentEndMs && previousEndMs >= currentStartMs
    }
    if (overlap) {
      return true
    }
  }
  return false
}

function getCreateDateConflictErrors(tenancy: TenancyWithDraftId, allTenancies: TenancyWithDraftId[]): TenancyDateConflictKey[] {
  const errors: TenancyDateConflictKey[] = []
  if (!tenancy.internalId || !tenancy.startingAt || !tenancy.objectId) {
    return errors
  }
  const startingAtMs = typeDateToMs(tenancy.startingAt)
  if (startingAtMs == null) {
    return errors
  }
  if (isEndingAtSet(tenancy.endingAt)) {
    const endingAtMs = typeDateToMs(tenancy.endingAt)
    if (endingAtMs != null && endingAtMs <= startingAtMs) {
      errors.push('message.savingErrors.tenancyStartingAtAfterEndingAt')
    }
  }
  const others = allTenancies.filter(c =>
    c &&
    c.objectId === tenancy.objectId &&
    c.vacancy === tenancy.vacancy &&
    !isSameTenancyRow(tenancy, c) &&
    c.startingAt
  )
  if (others.some(c => isSameTypeDate(tenancy.startingAt, c.startingAt))) {
    errors.push('message.savingErrors.sameDateOfTenanciesError')
  }
  return errors
}

function getOpenEndedTenancyErrors(
  tenancy: TenancyWithDraftId,
  allTenancies: TenancyWithDraftId[],
  debitUntil: TypeDate | null | undefined
): TenancyDateConflictKey[] {
  const errors: TenancyDateConflictKey[] = []
  // Treat API sentinels ({ year: -1 }, empty objects) as unset — same as hasOverlap.
  if (isEndingAtSet(tenancy.endingAt) || !tenancy.startingAt) {
    return errors
  }
  const startingAtMs = typeDateToMs(tenancy.startingAt)
  if (startingAtMs == null) {
    return errors
  }
  const objectTenancies = allTenancies.filter(c => c.objectId === tenancy.objectId)
  const afterwardsTenancies = objectTenancies.filter(c => {
    const cStartMs = typeDateToMs(c.startingAt)
    return cStartMs != null && startingAtMs < cStartMs
  })
  const sameDateOfTenancies = objectTenancies.filter(
    c =>
      !isSameTenancyRow(tenancy, c) &&
      c.vacancy === tenancy.vacancy &&
      typeDateToMs(c.startingAt) === startingAtMs
  )
  const sameDateTenancies = objectTenancies.filter(
    c =>
      !isSameTenancyRow(tenancy, c) &&
      isSameTypeDate(tenancy.startingAt, c.startingAt) &&
      c.activated &&
      isDebitUntilAfterStart(debitUntil, tenancy.startingAt)
  )
  if (sameDateTenancies.length > 0) {
    errors.push('message.savingErrors.sameDateTenanciesError')
  }
  if (sameDateOfTenancies.length > 0 && !tenancy.internalId) {
    errors.push('message.savingErrors.sameDateOfTenanciesError')
  }
  if (afterwardsTenancies.length > 0) {
    errors.push('message.savingErrors.tenancyEndAtCanNotBeDeleted')
  }
  return errors
}

function getVacancyStartsBeforePreviousErrors(
  vacancy: TenancyWithDraftId,
  allTenancies: TenancyWithDraftId[]
): TenancyDateConflictKey[] {
  const errors: TenancyDateConflictKey[] = []
  if (!vacancy.internalId || !vacancy.startingAt || vacancy.vacancy !== true) {
    return errors
  }
  const startingAtMs = typeDateToMs(vacancy.startingAt)
  if (startingAtMs == null) {
    return errors
  }
  const startsBeforePrevious = allTenancies.some(c => {
    if (c.id === vacancy.id || c.objectId !== vacancy.objectId || !isEndingAtSet(c.endingAt)) {
      return false
    }
    const endingAtMs = typeDateToMs(c.endingAt)
    return endingAtMs != null && startingAtMs <= endingAtMs
  })
  if (startsBeforePrevious) {
    errors.push('message.addVacancy.startsBeforePrevious')
  }
  return errors
}

function getOpenEndedVacancyErrors(
  vacancy: TenancyWithDraftId,
  allTenancies: TenancyWithDraftId[],
  debitUntil: TypeDate | null | undefined
): TenancyDateConflictKey[] {
  const errors: TenancyDateConflictKey[] = []
  // Treat API sentinels ({ year: -1 }, empty objects) as unset — same as hasOverlap.
  if (isEndingAtSet(vacancy.endingAt) || !vacancy.startingAt || vacancy.vacancy !== true) {
    return errors
  }
  const startingAtMs = typeDateToMs(vacancy.startingAt)
  if (startingAtMs == null) {
    return errors
  }
  const objectTenancies = allTenancies.filter(c => c.objectId === vacancy.objectId)
  const afterwardsTenancies = objectTenancies.filter(c => {
    const cStartMs = typeDateToMs(c.startingAt)
    return cStartMs != null && startingAtMs < cStartMs
  })
  const sameDateOfTenancies = objectTenancies.filter(
    c =>
      c.id !== vacancy.id &&
      c.vacancy === vacancy.vacancy &&
      typeDateToMs(c.startingAt) === startingAtMs
  )
  const sameDateTenancies = objectTenancies.filter(
    c =>
      c.id !== vacancy.id &&
      !vacancy.internalId &&
      isSameTypeDate(vacancy.startingAt, c.startingAt) &&
      vacancy.activated &&
      isDebitUntilAfterStart(debitUntil, c.startingAt)
  )
  if (sameDateTenancies.length > 0) {
    errors.push('message.savingErrors.sameDateTenanciesError')
  }
  if (sameDateOfTenancies.length > 0) {
    errors.push('message.savingErrors.sameDateOfTenanciesError')
  }
  if (afterwardsTenancies.length > 0 && !vacancy.internalId) {
    errors.push('message.savingErrors.vacancyEndAtCanNotBeDeleted')
  }
  return errors
}

function getConsecutiveVacancyReasonErrors(tenancies: TenancyWithDraftId[]): TenancyDateConflictKey[] {
  const errors: TenancyDateConflictKey[] = []
  const sorted = [...tenancies].sort((a, b) => {
    const aMs = typeDateToMs(a.startingAt) ?? 0
    const bMs = typeDateToMs(b.startingAt) ?? 0
    return aMs - bMs
  })
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i]
    const next = sorted[i + 1]
    if (current.vacancy === true && next.vacancy === true && current.vacancyReason === next.vacancyReason) {
      errors.push('message.savingErrors.reasonVacancyOnTwoVacancies')
      break
    }
  }
  return errors
}

/**
 * Collect all cross-tenancy/vacancy date conflict message keys for a single object.
 * Mirrors the rules used by CreateObjectsTab / CreateTenantsTab so both tabs stay consistent.
 * Bounded historical drafts (COOZ-15913) are not treated as overlapping the current contract.
 *
 * @param allTenancies - Full tenancy list from the onboarding store.
 * @param objectId - Object whose tenancies should be checked.
 * @param debitUntil - Object debitUntil date (used for same-date / debitUntil checks).
 */
export function getTenancyDateConflictMessageKeys(
  allTenancies: TenancyWithDraftId[],
  objectId: string,
  debitUntil: TypeDate | null | undefined
): TenancyDateConflictKey[] {
  const errors: TenancyDateConflictKey[] = []
  const objectTenancies = allTenancies.filter(c => c && c.objectId === objectId)
  if (objectTenancies.length === 0) {
    return errors
  }

  const newDraft = objectTenancies.find(t => t.internalId && !t.vacancy) ?? null
  const previous = newDraft
    ? findLatestPreviousNonVacancyTenancy(allTenancies, objectId, {
      id: newDraft.id,
      internalId: newDraft.internalId
    })
    : null
  const historicalMissingEnd = !!(previous && newDraft &&
    isHistoricalTenancyMissingEndDate(newDraft.startingAt, newDraft.endingAt, previous.startingAt))
  const boundedHistorical = !!(previous && newDraft &&
    isBoundedHistoricalTenancy(newDraft.startingAt, newDraft.endingAt, previous.startingAt))

  if (historicalMissingEnd) {
    errors.push('message.savingErrors.tenancyEndAtCanNotBeDeleted')
  }

  const nonVacancyTenancies = objectTenancies.filter(t => !t.vacancy)
  const nonVacancyRanges = nonVacancyTenancies.map(t => ({ start: t.startingAt, end: t.endingAt }))
  if (hasOverlap(nonVacancyRanges) && !historicalMissingEnd) {
    if (boundedHistorical && newDraft && previous) {
      const withoutDraft = nonVacancyTenancies
        .filter(t => !isSameTenancyRow(t, newDraft))
        .map(t => ({ start: t.startingAt, end: t.endingAt }))
      const withoutPrevious = nonVacancyTenancies
        .filter(t => !isSameTenancyRow(t, previous))
        .map(t => ({ start: t.startingAt, end: t.endingAt }))
      if (hasOverlap(withoutDraft) || hasOverlap(withoutPrevious)) {
        errors.push('message.savingErrors.tenancyStartingOverLap')
      }
    } else {
      errors.push('message.savingErrors.tenancyStartingOverLap')
    }
  }

  const consecutiveReasonErrors = getConsecutiveVacancyReasonErrors(objectTenancies)
  errors.push(...consecutiveReasonErrors)

  objectTenancies.forEach(tenancy => {
    const createErrors = getCreateDateConflictErrors(tenancy, allTenancies)
    errors.push(...createErrors)

    if (tenancy.vacancy === true) {
      const startsBeforeErrors = getVacancyStartsBeforePreviousErrors(tenancy, allTenancies)
      errors.push(...startsBeforeErrors)

      const openEndedVacancyErrors = getOpenEndedVacancyErrors(tenancy, allTenancies, debitUntil)
      errors.push(...openEndedVacancyErrors)
    } else if (!(historicalMissingEnd && newDraft && isSameTenancyRow(tenancy, newDraft))) {
      const openEndedErrors = getOpenEndedTenancyErrors(tenancy, allTenancies, debitUntil)
      errors.push(...openEndedErrors)
    }
  })

  return [...new Set(errors)]
}
